// CLOUDWEB-21024: схемы IDB debug-контуров web-push. Основные наполняет Service Worker,
// а читает страница (IndexedDB общая на origin — оттуда работают app.webPush.status()/logs()).
//
// Схема живёт в одном месте не для красоты: если читатель откроет базу без версии и она
// ещё не создана, он создаст её пустой — писатель потом не получит `onupgradeneeded`
// (версия совпадает), и все его транзакции будут падать на несуществующем сторе.
// Поэтому обе стороны открывают базу с этой версией и прогоняют `ensureStore`.

export interface SwIdbSchema {
    name: string;
    store: string;
    version: number;
    /** Ключи генерирует база (лента событий), а не вызывающий код. */
    autoIncrement?: boolean;
}

/** Кэш Omicron-конфига в SW (`serviceWorker/omicronConfig`): один рекорд по ключу. */
export const SW_CONFIG_DB: SwIdbSchema = { name: 'cloud-sw-config', store: 'config', version: 1 };

/** Дедуп показанных пушей (`serviceWorker/push/showedPushIds`): ключ — id пуша, значение — метка времени. */
export const SW_PUSH_IDS_DB: SwIdbSchema = { name: 'cloud-sw', store: 'showed-push-ids', version: 1 };

/** Debug-лог SW (`serviceWorker/debugLog`): лента событий пуша. */
export const SW_DEBUG_LOG_DB: SwIdbSchema = { name: 'webpush-debug-sw', store: 'logs', version: 1, autoIncrement: true };

/**
 * Debug-лог page context (`reactApp/modules/webPush/webPush.deeplink`): лента событий
 * диплинк-моста. Пишет и читает страница, но дескриптор держим рядом с SW-логом — обе ленты
 * отдаёт одна консольная команда, и расхождение имён/версий тут же ломало бы чтение.
 */
export const PAGE_DEBUG_LOG_DB: SwIdbSchema = { name: 'webpush-debug-page', store: 'logs', version: 1, autoIncrement: true };

/**
 * Состояние устройства (CLOUDWEB-21080, §2): переживает терминацию воркера, поэтому не переменная
 * модуля. Два фиксированных ключа в одном сторе, а не поле общего рекорда — у ключей разные
 * писатели, и общий рекорд означал бы гонку read-modify-write между ними на ровном месте.
 */
export const SW_DEVICE_STATE_DB: SwIdbSchema = { name: 'cloud-sw-device', store: 'state', version: 1 };

/** Рекорд `{visibleAt}` в `SW_DEVICE_STATE_DB`: метку присутствия пишет страница, читает воркер. */
export const DEVICE_STATE_KEY = 'device';

/** Рекорд `{appUrl, at}` в `SW_DEVICE_STATE_DB`: пишет и читает только воркер, страница не трогает. */
export const PENDING_DEEPLINK_KEY = 'pending-deeplink';

/** Создать стор, если его нет. Идемпотентно — безопасно вызывать с любой из сторон. */
export function ensureStore(db: IDBDatabase, schema: SwIdbSchema) {
    if (!db.objectStoreNames.contains(schema.store)) {
        db.createObjectStore(schema.store, schema.autoIncrement ? { autoIncrement: true } : undefined);
    }
}

/**
 * Сколько записей держим в debug-лентах. Ограничиваем количеством, а не временем: смысл
 * ленты в том, чтобы увидеть пуш, пришедший при закрытых DevTools, поэтому старые записи
 * ценны сами по себе — терять их по TTL нельзя, а расти бесконечно нельзя тоже.
 */
export const MAX_DEBUG_LOG_ENTRIES = 1000;

/**
 * Удалить самые старые записи, если их стало больше `maxEntries`. Ключи автоинкрементные и
 * монотонные, поэтому прямой курсор идёт от старых к новым.
 *
 * Своя транзакция, fire-and-forget, ничего не бросает: подрезка не должна ни ломать запись
 * события, ни задерживать её. Вызывающий может закрывать соединение сразу — `close()`
 * дожидается завершения уже открытых транзакций.
 */
export function trimStore(db: IDBDatabase, schema: SwIdbSchema, maxEntries: number) {
    try {
        const tx = db.transaction(schema.store, 'readwrite');
        const store = tx.objectStore(schema.store);
        const countReq = store.count();

        countReq.onsuccess = () => {
            let left = countReq.result - maxEntries;

            if (left <= 0) {
                return;
            }

            const cursorReq = store.openCursor();

            cursorReq.onsuccess = () => {
                const cursor = cursorReq.result;

                if (!cursor || left <= 0) {
                    return;
                }

                cursor.delete();
                left--;
                cursor.continue();
            };
        };
    } catch (_) {}
}
